import { buildCollection, buildProperty } from "@firecms/core";
import { HomeButton } from "../components/HomeButton";

export type CouponCampaign = {
    name: string;
    description: string;
    image_url: string;
    discount_type: string;
    discount_value: number;
    applicable_to: string[];
    max_supply: number;
    issued_count: number;
    deploy_status: string;
    is_active: boolean;
    valid_from: Date;
    valid_until: Date;
    created_at: Date;
    created_by: string;
};

export const couponCampaignsCollection = buildCollection<CouponCampaign>({
    id: "coupon_campaigns",
    name: "Coupon Campaigns",
    singularName: "Coupon Campaign",
    path: "coupon_campaigns",
    icon: "ConfirmationNumber",
    group: "Promotions",
    Actions: HomeButton,
    description: "Coupon campaigns for discounts",
    permissions: {
        edit: true,
        create: false,
        delete: false,
    },
    properties: {
        name: buildProperty({
            name: "Name",
            dataType: "string",
            validation: { required: true },
        }),
        description: buildProperty({
            name: "Description",
            dataType: "string",
            multiline: true,
        }),
        image_url: buildProperty({
            name: "Image URL",
            dataType: "string",
            url: true,
        }),
        discount_type: buildProperty({
            name: "Discount Type",
            dataType: "string",
            enumValues: {
                percent: "Percentage",
                flat: "Flat Amount (Stars)",
            },
            validation: { required: true },
        }),
        discount_value: buildProperty({
            name: "Discount Value",
            dataType: "number",
            description: "Percentage (0-100) or flat stars amount",
            validation: { required: true, min: 0 },
        }),
        applicable_to: buildProperty({
            name: "Applicable To",
            dataType: "array",
            of: {
                dataType: "string",
                enumValues: {
                    pro_monthly: "Pro Monthly Subscription",
                    pro_yearly: "Pro Yearly Subscription",
                    starter: "Starter Pack",
                    study_session: "Study Session Pack",
                    power_pack: "Power Pack",
                    weekly_pass: "Weekly Pass",
                },
            },
            description: "Which items this coupon can be used for",
        }),
        max_supply: buildProperty({
            name: "Max Supply",
            dataType: "number",
            description: "0 = unlimited",
            defaultValue: 0,
        }),
        issued_count: buildProperty({
            name: "Issued",
            dataType: "number",
            readOnly: true,
            defaultValue: 0,
        }),
        deploy_status: buildProperty({
            name: "Status",
            dataType: "string",
            readOnly: true,
            enumValues: {
                draft: "Draft",
                active: "Active",
                paused: "Paused",
                completed: "Completed",
            },
        }),
        is_active: buildProperty({
            name: "Active",
            dataType: "boolean",
            defaultValue: true,
            columnWidth: 100,
        }),
        valid_from: buildProperty({
            name: "Valid From",
            dataType: "date",
            mode: "date_time",
        }),
        valid_until: buildProperty({
            name: "Valid Until",
            dataType: "date",
            mode: "date_time",
            description: "Leave empty for no expiry",
        }),
        created_at: buildProperty({
            name: "Created At",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        created_by: buildProperty({
            name: "Created By",
            dataType: "string",
            readOnly: true,
        }),
    },
    propertiesOrder: [
        "is_active",
        "name",
        "description",
        "discount_type",
        "discount_value",
        "applicable_to",
        "max_supply",
        "issued_count",
        "deploy_status",
        "valid_from",
        "valid_until",
    ],
});
